import { useEffect, useState } from "react";
import { Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { OplyxLogo } from "@/components/brand/OplyxLogo";
import { LogOut, Wifi, WifiOff, Download } from "lucide-react";

/**
 * Iter 121 — StaffShell (refeito Iter 122)
 * - Auth gate: qualquer role logada pode acessar (incluindo owner/manager)
 * - Registra Service Worker dedicado (/sw-staff.js, escopo /staff)
 * - Substitui manifest dinâmico para /manifest-staff.json (PWA install)
 * - Banner "Adicionar à tela inicial" (beforeinstallprompt)
 * - Identidade visual alinhada à Landing: paleta #0B0D0F + accent #00FF88 + grid-bg sutil
 */
export default function StaffShell() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 1) Boot: manifest swap + SW + listeners
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const previous = link?.getAttribute("href");
    if (link) link.setAttribute("href", "/manifest-staff.json");
    document.title = "OPLYX Staff: comandas";
    const meta = document.querySelector('meta[name="theme-color"]');
    const previousTheme = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "#0B0D0F");

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw-staff.js", { scope: "/staff" })
        .catch((err) => console.warn("[staff-sw] register failed:", err));
    }

    const onBeforeInstall = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onAppInstalled = () => { setInstalled(true); setInstallPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      if (link && previous) link.setAttribute("href", previous);
      if (meta && previousTheme) meta.setAttribute("content", previousTheme);
    };
  }, []);

  // 2) Auth check
  useEffect(() => {
    const token = localStorage.getItem("oplyx_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem("oplyx_token"); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const onInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    setInstallPrompt(null);
  };

  const logout = () => {
    localStorage.removeItem("oplyx_token");
    localStorage.removeItem("oplyx_staff_mode");
    navigate("/staff/auth", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D0F] text-white flex items-center justify-center" data-testid="staff-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <div className="text-sm opacity-70 font-mono">Carregando OPLYX Staff...</div>
        </div>
      </div>
    );
  }

  // Não logado → /staff/auth
  if (!user) {
    if (location.pathname.startsWith("/staff/auth")) {
      return <Outlet context={{ onLogin: setUser }} />;
    }
    return <Navigate to="/staff/auth" replace />;
  }

  // Iter 122 — qualquer role logada pode acessar; owner/manager também (substitui o bloqueio anterior)
  return (
    <div className="min-h-screen bg-[#0B0D0F] text-white flex flex-col relative overflow-hidden"
         data-testid="staff-shell">
      {/* Background grid sutil — alinhado à Landing */}
      <div className="absolute inset-0 staff-grid-bg opacity-[0.07] pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FF88]/20 to-transparent pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 border-b border-white/5 px-4 py-3 sticky top-0 backdrop-blur bg-[#0B0D0F]/80">
        <div className="flex items-center justify-between gap-3 max-w-2xl mx-auto">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <OplyxLogo size={22} tone="light" wordmark={false} />
            <div className="border-l border-white/10 pl-3 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#00FF88] font-semibold">
                STAFF
              </div>
              <div className="text-sm font-medium truncate" data-testid="staff-user-name">
                {user.name || user.email}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span title={online ? "Conectado" : "Sem conexão"}>
              {online ? <Wifi className="w-3.5 h-3.5 text-[#00FF88]" /> : <WifiOff className="w-3.5 h-3.5 text-amber-400" />}
            </span>
            <Button size="sm" variant="ghost" onClick={logout} data-testid="staff-logout"
                    className="text-white/70 hover:text-white hover:bg-white/10 h-8 w-8 p-0">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Install banner */}
      {installPrompt && !installed && (
        <button
          onClick={onInstall}
          className="relative z-10 bg-[#00FF88] hover:bg-[#00ff88]/90 text-[#0B0D0F] text-sm py-2.5 flex items-center justify-center gap-2 transition w-full font-semibold"
          data-testid="staff-install-pwa"
        >
          <Download className="w-4 h-4" />
          Adicionar à tela inicial (abre como app)
        </button>
      )}

      {/* Conteúdo */}
      <main className="flex-1 overflow-y-auto pb-safe relative z-10 max-w-2xl w-full mx-auto">
        <Outlet />
      </main>

      <style>{`
        .staff-grid-bg {
          background-image:
            linear-gradient(rgba(0, 255, 136, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 136, 0.5) 1px, transparent 1px);
          background-size: 48px 48px;
        }
      `}</style>
    </div>
  );
}
